import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Phone, Zap,
  UtensilsCrossed, Building2, PlugZap, CircuitBoard, Wrench, FileCheck2,
} from "lucide-react";
import Footer from "@/components/Footer";

export const SERVICE_DETAILS = [
  {
    slug: "kitchen-bathroom",
    icon: UtensilsCrossed,
    title: "Kitchen & Bathroom Renovations",
    short: "Full electrical fit-outs for renos — lighting, power and finishes that lift the whole room.",
    long: "A reno lives or dies on its electrical. We handle the full fit-out — lighting design, pendant and feature lighting, powerpoints where you actually need them, appliance circuits and exhaust fans — coordinated with your builder so nothing holds up the job. Finished clean, tested and certified.",
    motion: { animate: { y: [0, -6, 0] }, transition: { duration: 3, repeat: Infinity, ease: "easeInOut" } },
  },
  {
    slug: "new-builds",
    icon: Building2,
    title: "New Builds — Homes, Granny Flats & Sheds",
    short: "From pre-lay to handover — complete electrical installation for any new dwelling.",
    long: "Full wiring for new homes, granny flats and sheds across Albany and the Great Southern. Mains, switchboard, lighting, power, data and Starlink-ready mounting — planned properly.",
    motion: { animate: { scale: [1, 1.12, 1] }, transition: { duration: 2.4, repeat: Infinity, ease: "easeInOut" } },
  },
  {
    slug: "powerpoints-rcds",
    icon: PlugZap,
    title: "PowerPoints, RCDs, Smoke Alarms & Downlights",
    short: "The essentials, installed and tested properly.",
    long: "The essentials that keep a home safe and liveable: extra powerpoints, safety switches (RCDs), interconnected smoke alarms and LED downlight upgrades — installed flush, level and tested properly, usually in a single visit.",
    motion: { animate: { opacity: [1, 0.35, 1, 0.6, 1] }, transition: { duration: 2.2, repeat: Infinity } },
  },
  {
    slug: "switchboard-upgrades",
    icon: CircuitBoard,
    title: "Switchboard Upgrades & Surge Protection",
    short: "Modern boards that protect your home, your gear and your family.",
    long: "Old ceramic fuses and crowded boards aren't just inconvenient — they're a risk. We upgrade to modern switchboards with proper circuit protection and whole-home surge protection, so your family and your appliances are covered.",
    motion: { animate: { rotate: [0, 7, -7, 0] }, transition: { duration: 3.2, repeat: Infinity, ease: "easeInOut" } },
  },
  {
    slug: "maintenance",
    icon: Wrench,
    title: "Residential & Commercial Maintenance",
    short: "Ongoing maintenance and rapid fault-finding for homes and businesses.",
    long: "Faults found fast, fixed properly. From a dead circuit at home to ongoing maintenance for shops, offices and rentals — one call, one local electrician, job done.",
    motion: { animate: { y: [0, 5, 0] }, transition: { duration: 2.6, repeat: Infinity, ease: "easeInOut" } },
  },
  {
    slug: "safety-certificates",
    icon: FileCheck2,
    title: "Safety Certificates",
    short: "Thorough inspections and certification you can hand over with confidence.",
    long: "Selling, leasing, or just want peace of mind? Thorough electrical safety inspections with clear certification you can hand to buyers, agents or insurers with confidence.",
    motion: { animate: { opacity: [1, 0.5, 1], scale: [1, 1.06, 1] }, transition: { duration: 3.4, repeat: Infinity, ease: "easeInOut" } },
  },
];

const ServicesPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const t = setTimeout(() => window.__lenis?.scrollTo(location.hash, { offset: -90 }), 600);
      return () => clearTimeout(t);
    }
  }, [location.hash]);

  return (
    <main className="bg-[#f7f8f6] pt-24 md:pt-32" data-testid="services-page">
      <section className="mx-auto max-w-7xl px-5 pb-20 md:px-10 md:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="mb-4 flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-petrol md:text-sm">
            <Zap className="h-3.5 w-3.5 animate-spark-flicker text-copper" strokeWidth={2} fill="currentColor" />
            Exchange Electrical — Albany WA
          </p>
          <h1 className="font-display text-5xl font-black uppercase leading-[0.95] tracking-tight text-petrol md:text-7xl">
            All services<span className="text-copper">.</span>
          </h1>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-ink/60 md:text-lg">
            Every job below is done by Derek — a local, licensed electrician with 15+
            years on the tools. Not sure which one you need? Call and describe the job.
          </p>
          <a
            href="tel:0490425408"
            data-testid="services-call-link"
            className="mt-6 inline-flex items-center gap-3 font-display text-2xl font-bold tracking-tight text-petrol transition-colors hover:text-ink"
          >
            <Phone className="h-5 w-5 text-copper" strokeWidth={1.5} />
            0490 425 408
          </a>
        </motion.div>

        <div className="mt-14 md:mt-20">
          {SERVICE_DETAILS.map((s, i) => (
            <motion.div
              key={s.slug}
              id={s.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="grid scroll-mt-28 grid-cols-12 items-start gap-4 border-t-2 border-ink py-8 last:border-b-2 md:py-12"
              data-testid={`service-detail-${s.slug}`}
            >
              <div className="col-span-2 flex items-center gap-3 md:col-span-1 md:flex-col md:items-start md:gap-4">
                <span className="font-display text-2xl font-black tracking-tight text-copper md:text-3xl">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <motion.span {...s.motion} className="inline-flex">
                  <s.icon className="h-6 w-6 text-petrol md:h-8 md:w-8" strokeWidth={1.5} />
                </motion.span>
              </div>
              <h2 className="col-span-10 pl-3 font-display text-xl font-bold uppercase tracking-tight text-ink md:col-span-4 md:pl-0 md:text-3xl">
                {s.title}
              </h2>
              <div className="col-span-10 col-start-3 pl-3 md:col-span-6 md:col-start-7 md:pl-0">
                <p className="text-sm leading-relaxed text-ink/70 md:text-base">{s.long}</p>
                <Link
                  to={`/quote?service=${encodeURIComponent(s.title)}`}
                  data-testid={`service-detail-quote-${s.slug}`}
                  className="group mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-petrol transition-colors hover:text-ink"
                >
                  Get a quote for this
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" strokeWidth={2} />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 flex flex-wrap items-center justify-center gap-4 md:mt-20"
        >
          <Link
            to="/quote"
            data-testid="services-page-quote-button"
            className="group inline-flex items-center gap-2 bg-copper px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-petrol hover:text-white"
          >
            Free Quote
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
          </Link>
          <a
            href="tel:0490425408"
            data-testid="services-page-call-button"
            className="inline-flex items-center gap-2 border-2 border-ink px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] text-ink transition-colors hover:border-copper hover:bg-copper"
          >
            <Phone className="h-4 w-4" strokeWidth={2} />
            0490 425 408
          </a>
        </motion.div>
      </section>
      <Footer />
    </main>
  );
};

export default ServicesPage;
